// src/components/Inquiries/InquiriesTable.js

import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";

import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
  TABLE_STYLES,
  TABLE_ROW_HOVER_SX,
  TABLE_ICON_BLUE_SX,
} from "../../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";

import { formatPhone } from "../../../utils/Phone";
import { formatLocationLabel } from "../companies/ProspectingCompaniesHelpers";
import {
  normalizeCountry,
  normalizeState,
  normalizeString,
} from "../../../utils/filterNormalization";

const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};
const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiInputBase-input": {
    py: 0.75,
    color: "#00336C",
    fontWeight: 700,
  },
  "& input": {
    color: "#00336C !important",
    WebkitTextFillColor: "#00336C",
    caretColor: "#00336C",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const STATUS_CONFIG = {
  New: { bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
  Converted: { bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  Closed: { bg: "#F5F5F5", color: "#374151", border: "#6B7280" },
};
const DEFAULT_STATUS_CONFIG = { bg: "#F3F4F6", color: "#9CA3AF", border: "#E5E7EB" };
const STATUS_TOOLTIPS = {
  New: "New inquiry awaiting review.",
  Converted: "Inquiry converted to a prospect company.",
  Closed: "Inquiry closed without conversion.",
};
const DEFAULT_STATUS_TOOLTIP = "Current inquiry status.";

function InquiriesTable({
  paginated,
  isSelected,
  toggleOne,
  toggleMany,
  allCurrentSelected,
  someCurrentSelected,
  navigate,
  setActiveInquiry,
  setProfileOpen,
  handleUpdateStatus,
  filters,
  onFilterChange,
  onLocationFilterChange,
  locationOptions,
}) {
  const STATUS_OPTIONS = ["New", "Converted", "Closed"];
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [statusChangeAnchor, setStatusChangeAnchor] = useState(null);
  const [statusChangeInquiry, setStatusChangeInquiry] = useState(null);
  const normalizeStatus = (status) =>
    STATUS_OPTIONS.includes(status) ? status : "New";
  const lowerSearch = filterSearch.trim().toLowerCase();
  const filteredLocationOptions = locationOptions.filter((option) =>
    option.label.toLowerCase().includes(lowerSearch),
  );

  const handleTogglePage = () => {
    const ids = paginated.map((i) => String(i.id));
    toggleMany(ids);
  };

  const openFilterMenu = (event, filterKey) => {
    setActiveFilter(filterKey);
    setFilterAnchor(event.currentTarget);
    setFilterSearch("");
  };

  const closeFilterMenu = () => {
    setActiveFilter(null);
    setFilterAnchor(null);
    setFilterSearch("");
  };

  const closeStatusMenu = () => {
    setStatusChangeAnchor(null);
    setStatusChangeInquiry(null);
  };

  const renderFilterHeader = ({ label, filterKey, active = false, minWidth }) => (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      justifyContent="center"
      sx={{ width: "100%" }}
    >
      <Button
        variant="text"
        onClick={(event) => openFilterMenu(event, filterKey)}
        sx={{
          ...HEADER_TRIGGER_SX,
          minWidth,
          color: active ? "#648315" : "#00336C",
          border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
          backgroundColor: "rgba(255,255,255,0.92)",
          "&:hover": {
            backgroundColor: "#ffffff",
            borderColor: active ? "#648315" : "rgba(255,255,255,0.8)",
          },
        }}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
      >
        {label}
      </Button>
    </Stack>
  );

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    const searchField = (placeholder) => (
      <Box sx={SEARCH_FIELD_SX}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={filterSearch}
          onChange={(event) => setFilterSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={placeholder}
          sx={FILTER_SEARCH_INPUT_SX}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
            },
          }}
        />
      </Box>
    );

    if (activeFilter === "company") {
      return (
        <Menu {...commonMenuProps}>
          <MenuItem
            selected={!filters.status}
            onClick={() => {
              onFilterChange("status", "");
              closeFilterMenu();
            }}
          >
            All Statuses
          </MenuItem>
          {STATUS_OPTIONS.map((option) => (
            <MenuItem
              key={option}
              selected={filters.status === option}
              onClick={() => {
                onFilterChange("status", option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "location") {
      const normalizedCity = normalizeString(filters.city);
      const normalizedState = normalizeState(filters.state_province);
      const normalizedCountry = normalizeCountry(filters.country);

      return (
        <Menu {...commonMenuProps}>
          {searchField("Search location")}
          <MenuItem
            selected={!filters.city && !filters.state_province && !filters.country}
            onClick={() => {
              onLocationFilterChange({ city: "", state_province: "", country: "" });
              closeFilterMenu();
            }}
          >
            All Locations
          </MenuItem>
          {filteredLocationOptions.map((option) => {
            const selected =
              normalizedCity === option.city &&
              normalizedState === option.state_province &&
              normalizedCountry === option.country;

            return (
              <MenuItem
                key={option.key}
                selected={selected}
                onClick={() => {
                  onLocationFilterChange({
                    city: option.city,
                    state_province: option.state_province,
                    country: option.country,
                  });
                  closeFilterMenu();
                }}
              >
                {option.label}
              </MenuItem>
            );
          })}
        </Menu>
      );
    }

    return null;
  };

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 980, md: 1100 }, borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center" sx={HEAD_CELL_SX}>
                <Checkbox
                  indeterminate={someCurrentSelected && !allCurrentSelected}
                  checked={allCurrentSelected}
                  onChange={handleTogglePage}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  indeterminateIcon={<RemoveCircleIcon />}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                  }}
                />
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>
                {renderFilterHeader({
                  label: "Company",
                  filterKey: "company",
                  active: Boolean(filters.status),
                  minWidth: 108,
                })}
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>
                {renderFilterHeader({
                  label: "Location",
                  filterKey: "location",
                  active: Boolean(filters.city || filters.state_province || filters.country),
                  minWidth: 124,
                })}
              </TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>Contact</TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>Phone</TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>Email</TableCell>
              <TableCell align="center" sx={HEAD_CELL_SX}>Inquiry Profile</TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{ background: "white" }}>
            {paginated.map((i, index) => {
              const striped = index % 2 === 0 ? "#fafafa" : "white";
              const statusValue = normalizeStatus(i.status);
              const statusCfg = STATUS_CONFIG[statusValue] || DEFAULT_STATUS_CONFIG;

              return (
                <TableRow
                  key={i.id}
                  sx={{ ...TABLE_ROW_HOVER_SX, backgroundColor: striped, "&:hover": { backgroundColor: "#eef2f7" } }}
                >
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      checked={isSelected(String(i.id))}
                      onChange={() => toggleOne(String(i.id))}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={TABLE_STYLES.bodyCell}
                  >
                    <Stack direction="column" spacing={0.4} alignItems="center">
                      <Tooltip title={i.company_name || ""}>
                        <Button
                          variant="text"
                          sx={{
                            ...TABLE_STYLES.clickableCell,
                            fontSize: "0.95rem",
                            fontWeight: "bold",
                          }}
                          onClick={() => navigate(`/inquiries/${i.id}`)}
                        >
                          {i.company_name || "—"}
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={STATUS_TOOLTIPS[statusValue] || DEFAULT_STATUS_TOOLTIP}
                        arrow
                        placement="top"
                      >
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusChangeAnchor(e.currentTarget);
                            setStatusChangeInquiry(i);
                          }}
                          endIcon={<ArrowDropDownIcon sx={{ fontSize: "0.8rem !important", ml: "-4px" }} />}
                          sx={{
                            height: 18,
                            fontSize: "0.63rem",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: 10,
                            px: 0.75,
                            py: 0,
                            minWidth: 0,
                            lineHeight: 1.2,
                            backgroundColor: statusCfg.bg,
                            color: statusCfg.color,
                            border: `1px solid ${statusCfg.border}`,
                            "&:hover": {
                              backgroundColor: statusCfg.bg,
                              borderColor: statusCfg.color,
                              opacity: 0.85,
                            },
                            "& .MuiButton-endIcon": { ml: 0.25 },
                          }}
                        >
                          {statusValue}
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    <Tooltip
                      title={formatLocationLabel({
                        city: i.city,
                        state: i.state_province,
                        country: i.country,
                      })}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatLocationLabel({
                          city: i.city,
                          state: i.state_province,
                          country: i.country,
                        })}
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    {i.contact_name || "-"}
                  </TableCell>

                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    {i.phone ? formatPhone(i.phone) : "-"}
                  </TableCell>

                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    {i.email ? (
                      <IconButton component="a" href={`mailto:${i.email}`}>
                        <EmailIcon sx={{ fontSize: 26, color: "#648315" }} />
                      </IconButton>
                    ) : (
                      <EmailIcon
                        sx={{
                          fontSize: 26,
                          color: "#648315",
                          opacity: 0.3,
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Explore Inquiry">
                      <IconButton
                        onClick={() => { setActiveInquiry(i); setProfileOpen(true); }}
                        sx={{
                          p: 0.5,
                          border: "1.5px solid #00336C",
                          borderRadius: 1.5,
                          "&:hover": {
                            backgroundColor: "rgba(0,51,108,0.06)",
                            borderColor: "#00336C",
                          },
                        }}
                      >
                        <ManageSearchIcon sx={TABLE_ICON_BLUE_SX} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {renderFilterMenu()}

      <Menu
        anchorEl={statusChangeAnchor}
        open={Boolean(statusChangeAnchor)}
        onClose={closeStatusMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 150,
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.15)",
              boxShadow: "0 8px 24px rgba(0,51,108,0.12)",
            },
          },
        }}
      >
        {STATUS_OPTIONS.map((status) => (
          <MenuItem
            key={status}
            selected={normalizeStatus(statusChangeInquiry?.status) === status}
            onClick={() => {
              if (statusChangeInquiry?.id) {
                handleUpdateStatus(statusChangeInquiry.id, status);
              }
              closeStatusMenu();
            }}
          >
            {status}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default InquiriesTable;
